import {
    getid,
    state,
    getLoginFlag,
    updateUserDisplay,
    isActiveTeamAdmin,
    canShowBioAgentsNav,
    clearActiveProjectFocus
} from './globals.js';
import { openConfirmModal } from './ui.js';
import { confirmAndSetActiveTeamId } from './teamScope.js';

import * as api from './api.js';

const BIO_AGENT_EXPERIMENT_ACK_KEY = 'ssq_bio_agent_experiment_ack_v1';
const BIO_AGENT_EXPERIMENT_LEGACY_ACK_KEY = 'ssq_chat_experiment_ack_v1';
const BIO_AGENT_NAV_COUNT_POLL_MS = 15000;
const BIO_AGENT_NAV_COUNT_BACKOFF_MAX_MS = 60000;

const isActiveBioAgentStatus = (status) => {
    const value = String(status || '').trim().toLowerCase();
    return value === 'running' || value === 'paused';
};

const setBioAgentsNavCount = (count) => {
    const safeCount = Math.max(0, Math.floor(Number.isFinite(Number(count)) ? Number(count) : 0));
    const label = safeCount > 9 ? '9+' : String(safeCount);
    const countEl = getid('bioAgentsActiveCount');
    const bioAgentsBtn = getid('bio_agents_btn');
    if (countEl) countEl.textContent = label;
    if (bioAgentsBtn) {
        const activeText = safeCount === 1 ? '1 active Bio Agent' : `${label} active Bio Agents`;
        bioAgentsBtn.setAttribute('aria-label', `Bio Agents, ${activeText}`);
        bioAgentsBtn.title = `Bio Agents: ${activeText}`;
    }
};

let bioAgentsNavCountSeq = 0;
let bioAgentsNavCountPromise = null;
let bioAgentsNavCountRequestToken = null;
let bioAgentsNavCountFailures = 0;
let bioAgentsNavCountTimer = null;

const clearBioAgentsNavCountTimer = () => {
    if (bioAgentsNavCountTimer === null) return;
    window.clearTimeout(bioAgentsNavCountTimer);
    bioAgentsNavCountTimer = null;
};

const canPollBioAgentsNavCount = () => {
    return !!getLoginFlag() && !!canShowBioAgentsNav();
};

const bioAgentsNavCountDelay = () => {
    const multiplier = Math.min(4, 2 ** Math.max(0, bioAgentsNavCountFailures));
    return Math.min(BIO_AGENT_NAV_COUNT_BACKOFF_MAX_MS, BIO_AGENT_NAV_COUNT_POLL_MS * multiplier);
};

function scheduleBioAgentsNavCountPoll(delayMs = null) {
    clearBioAgentsNavCountTimer();
    if (!canPollBioAgentsNavCount()) {
        bioAgentsNavCountSeq += 1;
        bioAgentsNavCountRequestToken = null;
        bioAgentsNavCountPromise = null;
        setBioAgentsNavCount(0);
        return;
    }
    if (document.hidden) return;
    const delay = delayMs === null ? bioAgentsNavCountDelay() : Math.max(0, Number(delayMs) || 0);
    bioAgentsNavCountTimer = window.setTimeout(async () => {
        bioAgentsNavCountTimer = null;
        await refreshBioAgentsNavCount();
        scheduleBioAgentsNavCountPoll();
    }, delay);
}

function wakeBioAgentsNavCountPoll() {
    if (document.hidden) {
        clearBioAgentsNavCountTimer();
        return;
    }
    scheduleBioAgentsNavCountPoll(0);
}

async function refreshBioAgentsNavCount() {
    if (!canPollBioAgentsNavCount()) {
        bioAgentsNavCountSeq += 1;
        bioAgentsNavCountRequestToken = null;
        bioAgentsNavCountPromise = null;
        setBioAgentsNavCount(0);
        return 0;
    }
    if (document.hidden) return 0;
    if (bioAgentsNavCountPromise) return bioAgentsNavCountPromise;
    const seq = ++bioAgentsNavCountSeq;
    const token = {};
    bioAgentsNavCountRequestToken = token;
    const promise = (async () => {
        try {
            const data = await api.listBioAgentThreads('all');
            if (seq !== bioAgentsNavCountSeq) return 0;
            const threads = Array.isArray(data?.threads) ? data.threads : [];
            const activeCount = threads.filter((thread) => isActiveBioAgentStatus(thread?.status)).length;
            setBioAgentsNavCount(activeCount);
            bioAgentsNavCountFailures = 0;
            return activeCount;
        } catch (err) {
            if (seq === bioAgentsNavCountSeq) {
                bioAgentsNavCountFailures = Math.min(bioAgentsNavCountFailures + 1, 6);
                setBioAgentsNavCount(0);
            }
            return 0;
        }
    })();
    bioAgentsNavCountPromise = promise;
    promise.finally(() => {
        if (bioAgentsNavCountRequestToken === token) {
            bioAgentsNavCountRequestToken = null;
            bioAgentsNavCountPromise = null;
        }
    });
    return promise;
}

window.refreshBioAgentsNavCount = refreshBioAgentsNavCount;

// Event listeners initialization
function initEventListeners() {
    const themeLink = document.getElementById('themeStylesheet');
    let activeTheme = null;
    const applyTheme = (theme) => {
        const nextTheme = theme === 'light' ? 'light' : 'dark';
        if (themeLink) {
            const url = new URL(themeLink.href, window.location.href);
            const search = url.search || '';
            const href = nextTheme === 'light' ? `/css/light.css${search}` : `/css/dark.css${search}`;
            if (!themeLink.href.endsWith(href)) {
                themeLink.href = href;
            }
        }
        localStorage.setItem('theme', nextTheme);
        const toggle = document.getElementById('themeToggle');
        if (toggle && toggle instanceof HTMLInputElement) {
            toggle.checked = nextTheme === 'light';
        }
        if (activeTheme !== nextTheme) {
            activeTheme = nextTheme;
            document.dispatchEvent(new CustomEvent('themechange', { detail: { theme: nextTheme } }));
        }
    };
    // Apply saved theme early in init (default to light)
    let initialTheme = 'light';
    try {
        const stored = localStorage.getItem('theme');
        if (stored === 'light' || stored === 'dark') {
            initialTheme = stored;
        }
    } catch {}
    applyTheme(initialTheme);

    const loginContainer = getid('loginContainer');
    const creditsInputContainer = getid('creditsInputContainer');
    const accountMenu = getid('accountMenu');
    const teamMenu = getid('teamMenu');
    const screenOverlay = document.getElementById('screenOverlay');
    const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    const sidePane = getid('sidePane');
    const sidePaneStorageKey = 'ssq_sidepane_open';
    const dropdownMenus = [loginContainer, creditsInputContainer, accountMenu, teamMenu].filter(Boolean);
    const dropdownOpen = (el) => !!el && !el.classList.contains('hidden');
    const hideDropdowns = (except = null) => dropdownMenus.forEach((el) => {
        if (el !== except) el.classList.add('hidden');
    });
    const persistSidePane = () => {
        if (!sidePane || isMobile()) return;
        try {
            localStorage.setItem(sidePaneStorageKey, sidePane.classList.contains('show') ? '1' : '0');
        } catch {}
    };
    const restoreSidePane = () => {
        if (!sidePane || isMobile()) return;
        let shouldShow = true;
        try {
            const saved = localStorage.getItem(sidePaneStorageKey);
            if (saved === '0' || saved === '1') {
                shouldShow = saved === '1';
            }
        } catch {}
        sidePane.classList.toggle('show', shouldShow);
    };
    const anyDropdownOpen = () => dropdownMenus.some(dropdownOpen) || (sidePane && sidePane.classList.contains('show'));
    restoreSidePane();
    const updateOverlay = () => {
        if (!screenOverlay) return;
        const show = isMobile() && anyDropdownOpen();
        screenOverlay.classList.toggle('hidden', !show);
        if (show) {
            screenOverlay.addEventListener('click', closeAll, { once: true });
        }
    };
    const closeAll = () => {
        hideDropdowns();
        sidePane?.classList.remove('show');
        updateOverlay();
    };
    const toggleDropdown = (target) => {
        target.classList.toggle('hidden');
        hideDropdowns(target);
        if (isMobile() && dropdownOpen(target)) sidePane?.classList.remove('show');
        updateOverlay();
    };
    const navigateAndHide = (path, menu) => {
        window.navigate(path);
        menu?.classList.add('hidden');
        updateOverlay();
    };
    const confirmLogout = async () => {
        const impersonating = state.isOwner && !!state.viewAsEmail;
        const ok = await openConfirmModal({
            title: impersonating ? 'Exit Impersonation' : 'Logout',
            message: impersonating ? 'Exit this impersonated account?' : 'Are you sure you want to log out?',
            confirmText: impersonating ? 'Exit' : 'Logout',
            cancelText: 'Cancel'
        });
        if (ok) await api.logout();
    };
    // Login dropdown toggle
    getid('loginButton').addEventListener('click', (e) => {
        e.stopPropagation();
        toggleDropdown(loginContainer);
    });
    getid('credits-disp').addEventListener('click', (e) => {
        e.stopPropagation();
        if (!getLoginFlag()) return;
        if (state.activeTeamId && !isActiveTeamAdmin()) {
            creditsInputContainer.classList.add('hidden');
            updateOverlay();
            return;
        }
        toggleDropdown(creditsInputContainer);
    });
    getid('email-disp').addEventListener('click', (e) => {
        e.stopPropagation();
        if (!getLoginFlag()) return;
        toggleDropdown(accountMenu);
    });
    // Close login container when clicking outside
    document.addEventListener('click', (e) => {
        if (!loginContainer.contains(e.target) && e.target !== getid('loginButton')) {
            loginContainer.classList.add('hidden');
        }
        if (!creditsInputContainer.contains(e.target) && e.target !== getid('credits-disp')) {
            creditsInputContainer.classList.add('hidden');
        }
        if (!accountMenu.contains(e.target) && e.target !== getid('email-disp')) {
            accountMenu.classList.add('hidden');
        }
        if (!teamMenu.contains(e.target) && e.target !== getid('activeTeamDisplay')) {
            teamMenu.classList.add('hidden');
        }
        updateOverlay();
    });

    const OTP_RESEND_SECONDS = 30;
    let otpResendTimer = null;
    let otpRequestInFlight = false;

    const showOTPEntry = () => {
        getid('e-group').classList.add('hidden');
        getid('o-group').classList.remove('hidden');
        getid('otpInput').focus();
    };

    const setOTPResendCooldown = (seconds = OTP_RESEND_SECONDS) => {
        const btn = getid('otpResend');
        if (!btn) return;
        if (otpResendTimer) {
            clearInterval(otpResendTimer);
            otpResendTimer = null;
        }

        let remaining = Math.max(0, Math.ceil(Number(seconds) || 0));
        const reset = () => {
            btn.disabled = false;
            btn.textContent = 'Send again';
        };
        if (remaining <= 0) {
            reset();
            return;
        }

        const tick = () => {
            if (remaining <= 0) {
                clearInterval(otpResendTimer);
                otpResendTimer = null;
                reset();
                return;
            }
            btn.disabled = true;
            btn.textContent = `Send again (${remaining}s)`;
            remaining -= 1;
        };
        tick();
        otpResendTimer = setInterval(tick, 1000);
    };

    const requestLoginCode = async (email, button, { resend = false } = {}) => {
        const cleanEmail = String(email || '').trim().toLowerCase();
        if (!cleanEmail) return false;
        if (otpRequestInFlight) return false;
        otpRequestInFlight = true;

        const originalText = button ? button.textContent : '';
        if (button) {
            button.disabled = true;
            button.textContent = 'Sending...';
        }

        let ok = false;
        try {
            const result = await api.reqOTP(cleanEmail, { resend });
            if (result) {
                ok = true;
                showOTPEntry();
                const retryAfter = Number(result.retry_after_seconds);
                setOTPResendCooldown(Number.isFinite(retryAfter) ? retryAfter : OTP_RESEND_SECONDS);
            }
            return result;
        } finally {
            otpRequestInFlight = false;
            if (button && (button.id !== 'otpResend' || !ok)) {
                button.disabled = false;
                button.textContent = originalText;
            }
        }
    };

    getid('emailSubmit').addEventListener('click', async (e) => {
        await requestLoginCode(getid('emailInput').value, e.currentTarget);
    });

    getid('otpResend').addEventListener('click', async (e) => {
        const email = state.tempEmail || getid('emailInput').value;
        await requestLoginCode(email, e.currentTarget, { resend: true });
    });

    const toggleSidePane = (e) => {
        e.preventDefault();
        const sp = sidePane;
        if (!sp) return;
        const willShow = !sp.classList.contains('show');
        sp.classList.toggle('show');
        // If opening sidebar on mobile, hide any open dropdowns
        if (isMobile() && willShow) {
            loginContainer.classList.add('hidden');
            creditsInputContainer.classList.add('hidden');
            accountMenu.classList.add('hidden');
        }
        updateOverlay();
        persistSidePane();
    };
    getid('menuToggle').addEventListener('click', toggleSidePane);
    const titleLink = document.getElementById('titleLink');
    if (titleLink) {
        titleLink.addEventListener('click', (e) => {
            if (isMobile()) {
                toggleSidePane(e);
            } else {
                window.navigate('/');
            }
        });
    }

    getid('otpSubmit').addEventListener('click', async () => {
        const otp = getid('otpInput').value.trim();
        await api.login(otp);
        updateUserDisplay();
    });

    // Theme toggle
    const themeBtn = document.getElementById('themeToggle');
    if (themeBtn && themeBtn instanceof HTMLInputElement) {
        themeBtn.addEventListener('change', (e) => {
            const checked = e.target.checked;
            applyTheme(checked ? 'light' : 'dark');
        });
    }

    getid('logout_btn').addEventListener('click', async () => {
        await confirmLogout();
    });

    // Account dropdown actions
    getid('accountTxsBtn').addEventListener('click', () => {
        navigateAndHide('/funding', accountMenu);
    });
    const keysBtn = getid('accountKeysBtn');
    if (keysBtn) {
        keysBtn.addEventListener('click', () => {
            navigateAndHide('/keys', accountMenu);
        });
    }
    const teamsBtn = getid('accountTeamsBtn');
    if (teamsBtn) {
        teamsBtn.addEventListener('click', () => {
            navigateAndHide('/teams', accountMenu);
        });
    }
    const activeTeamDisplay = getid('activeTeamDisplay');
    if (activeTeamDisplay) {
        activeTeamDisplay.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!getLoginFlag() || !state.activeTeamId) return;
            toggleDropdown(teamMenu);
        });
    }
    const projectFocusClear = getid('projectFocusClear');
    if (projectFocusClear) {
        projectFocusClear.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            clearActiveProjectFocus();
            if (typeof window.route === 'function') {
                window.route();
            }
        });
    }
    const teamManageBtn = getid('teamManageBtn');
    if (teamManageBtn) {
        teamManageBtn.addEventListener('click', () => {
            navigateAndHide('/team', teamMenu);
        });
    }
    const teamFundingBtn = getid('teamFundingBtn');
    if (teamFundingBtn) {
        teamFundingBtn.addEventListener('click', () => {
            navigateAndHide('/funding', teamMenu);
        });
    }
    const teamKeysBtn = getid('teamKeysBtn');
    if (teamKeysBtn) {
        teamKeysBtn.addEventListener('click', () => {
            navigateAndHide('/keys', teamMenu);
        });
    }
    const teamExitBtn = getid('teamExitBtn');
    if (teamExitBtn) {
        teamExitBtn.addEventListener('click', async () => {
            teamMenu.classList.add('hidden');
            updateOverlay();
            const ok = await confirmAndSetActiveTeamId(0);
            if (!ok) return;
            try {
                await api.loadUserInfo();
                updateUserDisplay();
                if (typeof window.route === 'function') {
                    window.route();
                }
            } catch (err) {
                console.warn('Failed to exit team scope:', err);
            }
        });
    }
    getid('accountLogoutBtn').addEventListener('click', async () => {
        accountMenu.classList.add('hidden');
        await confirmLogout();
    });

    const isBioAgentExperimentEnabled = () => {
        try {
            return localStorage.getItem(BIO_AGENT_EXPERIMENT_ACK_KEY) === '1'
                || localStorage.getItem(BIO_AGENT_EXPERIMENT_LEGACY_ACK_KEY) === '1';
        } catch {
            return false;
        }
    };
    const setBioAgentExperimentEnabled = (enabled) => {
        try {
            if (enabled) {
                localStorage.setItem(BIO_AGENT_EXPERIMENT_ACK_KEY, '1');
            } else {
                localStorage.removeItem(BIO_AGENT_EXPERIMENT_ACK_KEY);
                localStorage.removeItem(BIO_AGENT_EXPERIMENT_LEGACY_ACK_KEY);
            }
        } catch {}
    };
    let bioAgentConsentPromptOpen = false;
    const ensureBioAgentExperimentConsent = async () => {
        if (isBioAgentExperimentEnabled()) return true;
        if (bioAgentConsentPromptOpen) return false;
        bioAgentConsentPromptOpen = true;
        try {
            const ok = await openConfirmModal({
                title: 'Enable Bio Agents',
                message: 'Bio Agents can use tools, including data deletion and running jobs which use your credits. (You can also use the <a href="/docs/mcp">MCP server</a> with custom agents for more control.) Enable if you accept these risks.',
                confirmText: 'Enable',
                cancelText: 'Cancel',
            });
            if (ok) {
                setBioAgentExperimentEnabled(true);
                return true;
            }
            return false;
        } finally {
            bioAgentConsentPromptOpen = false;
        }
    };
    const handleBioAgentsNavClick = async (event) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }
        const consented = await ensureBioAgentExperimentConsent();
        if (!consented) return;
        window.navigate('/bio-agents');
    };
    window.handleBioAgentsNavClick = handleBioAgentsNavClick;
    const bioAgentsBtn = getid('bio_agents_btn');
    if (bioAgentsBtn) {
        bioAgentsBtn.onclick = handleBioAgentsNavClick;
    }

    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            clearBioAgentsNavCountTimer();
        } else {
            wakeBioAgentsNavCountPoll();
        }
    });
    document.addEventListener('ssq-user-display-changed', () => wakeBioAgentsNavCountPoll());
    window.addEventListener('focus', () => wakeBioAgentsNavCountPoll());
    scheduleBioAgentsNavCountPoll(0);

    document.querySelectorAll('.nav-btn').forEach((el) => {
        el.addEventListener('click', (e) => {
            if (isMobile() && sidePane) {
                sidePane.classList.remove('show');
            }
        });
    });

    const creditsInput = getid('creditsInput');
    const creditsSubmit = getid('creditsSubmit');
    let paymentTopUpMaxCredits = null;
    api.getPricing()
        .then((pricing) => {
            const max = Number(pricing?.top_up_max_credits);
            if (Number.isFinite(max) && max > 0) {
                paymentTopUpMaxCredits = Math.floor(max);
                creditsInput.max = String(paymentTopUpMaxCredits);
            }
        })
        .catch(() => {});

    creditsSubmit.addEventListener('click', async () => {
        let amount = parseInt(creditsInput.value);
        if (isNaN(amount) || amount < 1) return;
        if (paymentTopUpMaxCredits != null && amount > paymentTopUpMaxCredits) {
            amount = paymentTopUpMaxCredits;
            creditsInput.value = String(amount);
        }
        await api.addCredits(amount);
    });
    
    const clickOnEnter = (input, button) => input?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') button?.click();
    });
    clickOnEnter(creditsInput, creditsSubmit);

    creditsInput.addEventListener('input', (e) => {
        e.target.value = Math.round(e.target.value);
        // Keep browser input aligned with the server-advertised payment bounds.
        if (e.target.value < 0) e.target.value = 0;
        if (paymentTopUpMaxCredits != null && e.target.value > paymentTopUpMaxCredits) {
            e.target.value = paymentTopUpMaxCredits;
        }
    });

    clickOnEnter(getid('emailInput'), getid('emailSubmit'));
    clickOnEnter(getid('otpInput'), getid('otpSubmit'));
}

// Adjust header layout on mobile: center title when header-buttons wrap
function updateHeaderLayout() {
    try {
        const header = document.querySelector('.header');
        const title = document.querySelector('.title-menu');
        const right = document.querySelector('.header-buttons');
        if (!header || !title || !right) return;
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
        const loggedIn = !!getLoginFlag();
        // Force two rows on mobile when logged in; otherwise detect wrap
        let twoRows = (isMobile && loggedIn);
        if (!twoRows) {
            const rt = right.getBoundingClientRect().top;
            const tt = title.getBoundingClientRect().top;
            twoRows = (right.offsetTop > title.offsetTop + 2) || (rt - tt > 4) || (header.scrollWidth > header.clientWidth);
        }
        header.classList.toggle('two-rows', twoRows);
    } catch {}
}

// Expose for cross-module updates (e.g., after login/logout UI changes)
// This lets globals js request a layout recalculation after it toggles header elements
window.updateHeaderLayout = updateHeaderLayout;

// Initialize app
document.addEventListener('DOMContentLoaded', async () => {
    initEventListeners();
    // Reflect current auth state immediately
    try { updateUserDisplay(); } catch {}
    // Magic link login (email + otp in query string)
    try {
        const params = new URLSearchParams(window.location.search);
        const magicOTP = params.get('otp');
        const magicEmail = params.get('email');
        const hasMagic = params.get('magic') === '1' || (magicOTP && magicEmail);
        if (hasMagic && magicOTP && magicEmail) {
            state.tempEmail = magicEmail;
            const url = new URL(window.location.href);
            url.searchParams.delete('magic');
            url.searchParams.delete('email');
            url.searchParams.delete('otp');
            window.history.replaceState({}, '', url.pathname + url.search);
            await api.login(magicOTP);
            updateUserDisplay();
        }
    } catch {}
    // If logged in, fetch user info (email/credits) and refresh header
    try {
        if (getLoginFlag()) {
            await api.loadUserInfo();
            updateUserDisplay();
        }
    } catch {}

    // If redirected back from Stripe, verify payment and refresh credits
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('payment') === 'success') {
            const sessionId = params.get('session_id');
            const txId = params.get('tx');
            if (sessionId && txId && getLoginFlag()) {
                await api.verifyPayment(sessionId, txId);
                await api.loadUserInfo();
                updateUserDisplay();
            }
            // Clean URL
            const url = new URL(window.location.href);
            url.searchParams.delete('payment');
            url.searchParams.delete('session_id');
            url.searchParams.delete('tx');
            window.history.replaceState({}, '', url.pathname + url.search);
            // Navigate to funding history for visibility
            window.navigate('/funding');
        }
    } catch {}
});
